import {Box, styled, SxProps, Typography} from '@mui/material';
import {DateTime} from 'luxon';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import React, {useMemo} from 'react';
import {textColorGray} from '../../styles';
import {useTranslation} from 'react-i18next';
import {SessionDetail} from '../../interfaces';
import {isNullOrUndefined} from '../../helpers';
import GameResult from './GameResult';

const SessionDate = styled(Typography)(() => ({
  color: textColorGray,
  marginBottom: 5,
}));

const SessionLength = styled(Typography)(() => ({
  color: textColorGray,
}));

interface Props {
  startTime?: Date;
  endTime?: Date;
  type: string;
  sxTitleAndLengthBox?: SxProps;
  localDisplayStartTime?: string;
  hasVideo?: boolean;
  hasPendingPlayerSessions?: boolean;
  teamName?: string | null;
  sessionDetail?: SessionDetail;
  timezone?: string;
}

const SessionInfo: React.FC<Props> = ({
  startTime,
  endTime,
  type,
  sxTitleAndLengthBox,
  hasVideo,
  hasPendingPlayerSessions,
  teamName,
  localDisplayStartTime,
  sessionDetail,
  timezone,
}) => {
  const {t} = useTranslation();

  const displayStartTime = useMemo(() => {
    if (localDisplayStartTime) {
      return `${DateTime.fromFormat(
        localDisplayStartTime,
        'M/d/yyyy h:mm a',
      ).toFormat('EEEE M/d/yyyy h:mm a')} ${timezone ?? ''}`;
    }
    if (startTime) {
      return `${DateTime.fromJSDate(startTime).toFormat('EEEE M/d/yyyy h:mm a ZZZZ')}`;
    }
    return '';
  }, [localDisplayStartTime, startTime, timezone]);

  return (
    <Box>
      {displayStartTime && (
        <SessionDate variant="body1">{displayStartTime}</SessionDate>
      )}
      <Box sx={{display: 'flex', flexDirection: 'row'}}>
        <Box sx={sxTitleAndLengthBox}>
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <Typography variant="h4">
              <em>
                {(type ?? '').toUpperCase()}
                {teamName && ` - ${teamName}`}
              </em>
            </Typography>
            {hasVideo && (
              <OndemandVideoIcon
                sx={{color: 'primary.main', ml: 1}}
                titleAccess={t('sessionFilter.hasVideo') as string}
              />
            )}
            {hasPendingPlayerSessions && (
              <CloudUploadIcon
                sx={{color: 'primary.main', ml: 1}}
                titleAccess={
                  t('sessionFilter.hasPendingPlayerSessions') as string
                }
              />
            )}
          </Box>
          {sessionDetail?.locationType &&
            sessionDetail?.opponentName &&
            !isNullOrUndefined(sessionDetail?.teamScore) &&
            !isNullOrUndefined(sessionDetail?.opponentScore) && (
              <GameResult
                locationType={sessionDetail?.locationType}
                opponentName={sessionDetail?.opponentName}
                teamScore={sessionDetail?.teamScore as number}
                opponentScore={sessionDetail?.opponentScore as number}
                typographyVariant="h5"
              />
            )}
          {startTime && endTime && (
            <SessionLength variant="body2">
              {`${Math.ceil(
                DateTime.fromJSDate(endTime).diff(
                  DateTime.fromJSDate(startTime),
                  'minutes',
                ).minutes,
              )} min`}
            </SessionLength>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SessionInfo;

SessionInfo.defaultProps = {
  sxTitleAndLengthBox: {},
};
